import { useState, FormEvent } from "react";

type SubmitStatus = "idle" | "loading" | "success" | "error";

export default function ContactSection() {
  const [charCount, setCharCount] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    business: "",
    service: "",
    message: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "message") setCharCount(value.length);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;

    setSubmitStatus("loading");

    const params = new URLSearchParams();
    params.append("name", formData.name);
    params.append("email", formData.email);
    params.append("business", formData.business || "N/A");
    params.append("service", formData.service || "N/A");
    params.append("message", formData.message);

    try {
      const res = await fetch("https://readdy.ai/api/form/d78sadrll6jbq2vddf20", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      if (res.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", business: "", service: "", message: "" });
        setCharCount(0);
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  return (
    <section id="contact" className="py-28 bg-[#080b18]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left Info */}
          <div>
            <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">Contact Us</span>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-6">
              Let&apos;s Build<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Something Great</span>
            </h2>
            <p className="text-white/50 text-base leading-relaxed mb-10">
              Ready to start growing your business? Tell us about yourself and we&apos;ll reach out to talk strategy — how a website can bring you more clients, more bookings, and more revenue.
            </p>

            {/* Contact Info */}
            <div className="space-y-5 mb-10">
              {[
                { icon: "ri-mail-send-line", label: "Email Us", value: "gkvertexconsulting@gmail.com" },
                { icon: "ri-phone-line", label: "Call Us", value: "347-814-4383 / 914-584-5989" },
                { icon: "ri-time-line", label: "Response Time", value: "Within 24 hours" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 flex-shrink-0">
                    <i className={`${item.icon} text-[#d4af37] text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-white/40 text-xs">{item.label}</div>
                    <div className="text-white text-sm font-medium">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-[#0c0f1c] border border-white/5 rounded-2xl p-8">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-white/50 text-xs font-medium mb-2 block tracking-wide">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="w-full bg-[#080b18] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4af37]/40 transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-medium mb-2 block tracking-wide">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="w-full bg-[#080b18] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4af37]/40 transition-colors duration-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/50 text-xs font-medium mb-2 block tracking-wide">Business Name</label>
                <input
                  type="text"
                  name="business"
                  value={formData.business}
                  onChange={handleChange}
                  placeholder="Your Company Name"
                  className="w-full bg-[#080b18] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4af37]/40 transition-colors duration-200"
                />
              </div>

              <div>
                <label className="text-white/50 text-xs font-medium mb-2 block tracking-wide">Service Interested In</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full bg-[#080b18] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#d4af37]/40 transition-colors duration-200 cursor-pointer"
                >
                  <option value="">Select a service...</option>
                  <option value="Website Design">Website Design</option>
                  <option value="Website Development">Website Development</option>
                  <option value="SEO Optimization">SEO Optimization</option>
                  <option value="Conversion Optimization">Conversion Optimization</option>
                  <option value="Full Package">Full Package</option>
                </select>
              </div>

              <div>
                <label className="text-white/50 text-xs font-medium mb-2 block tracking-wide">
                  Tell Us About Your Project
                  <span className={`ml-2 ${charCount > 450 ? "text-red-400" : "text-white/30"}`}>({charCount}/500)</span>
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your business, goals, and what you're looking for..."
                  className="w-full bg-[#080b18] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4af37]/40 transition-colors duration-200 resize-none"
                />
              </div>

              {submitStatus === "success" && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <i className="ri-checkbox-circle-line text-green-400 text-xl"></i>
                  <div>
                    <p className="text-green-400 text-sm font-semibold">Message sent!</p>
                    <p className="text-white/40 text-xs">We&apos;ll get back to you within 24 hours.</p>
                  </div>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <i className="ri-error-warning-line text-red-400 text-xl"></i>
                  <p className="text-red-400 text-sm">Something went wrong. Please try again or email us directly.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={charCount > 500 || submitStatus === "loading"}
                className="w-full py-4 rounded-xl bg-[#d4af37] text-[#050810] font-bold text-sm tracking-wide hover:bg-[#e8c84a] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {submitStatus === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <i className="ri-mail-send-line"></i>
                  </>
                )}
              </button>


            </form>
          </div>
        </div>
      </div>
    </section>
  );
}